import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { PhoneCall, PhoneOff, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../api/axiosInstance';
import API_ENDPOINTS from '../api/endpoints';
import { useCallStore, CALL_STATUS } from '../stores/callStore';
import { isZombieCall } from '../utils/zombieCall';
import { startPhoneLeadership } from '../utils/phoneTabLock';
import useNicePhoneSSE from '../hooks/useNicePhoneSSE';
import NicePhoneToolbar, { setNicePhoneClickToCallMode, hasLiveSipSession, isNicePhoneWidgetReady } from './NicePhoneToolbar';
import { isAdmin, canMakeCalls } from '../utils/roles';
import { leadFlowLog } from '../utils/leadFlowLogger';
import styles from './NicePhoneCallSync.module.css';

const LEADS_PATH = '/my-leads';
const RECONCILE_INTERVAL_MS = 30000;

const leadIdOf = (lead) => {
  const id = lead?.leadID ?? lead?.id;
  return id == null ? '' : String(id);
};

/**
 * Global NicePhone call lifecycle — mounted once in Layout so a call that ends
 * while the agent is on another page still flips the store to
 * ENDED_PENDING_DISPOSITION (which opens the global DispositionModal).
 *
 * Sources of truth, in order:
 *  1. SSE `call_ended` from the backend webhook
 *  2. Vendor browser events (`nicedial:call-end`) from the SIP snippet
 *  3. Reconciliation against GET /nicephone/active-call whenever we could have
 *     missed 1 or 2 (mount, SSE reconnect, tab visible again, route change,
 *     and a slow heartbeat while a call is marked active)
 */
const NicePhoneCallSync = () => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const { callStatus, currentLead, endCall, startCall } = useCallStore();
  const reconcilingRef = useRef(false);

  // The webphoneType check is gone: NicePhone is the only webphone, so call
  // lifecycle sync applies to every calling user.
  /**
   * Only one tab owns the phone. Every other CRM tab mounts this component too,
   * and a follower must not run the call lifecycle: it holds no SIP session of
   * its own, so its reconcile would read a live call as dead and raise the
   * disposition modal for a call still running in the leader tab.
   */
  const [isPhoneLeader, setIsPhoneLeader] = useState(false);
  useEffect(() => startPhoneLeadership(setIsPhoneLeader), []);

  const canUsePhone = !!user && !isAdmin(user) && canMakeCalls(user);
  const enabled = canUsePhone && isPhoneLeader;

  const markEnded = useCallback((source, label) => {
    const state = useCallStore.getState();
    if (state.callStatus !== CALL_STATUS.CALLING) return false;
    leadFlowLog.effect(`call ended via ${source}`, { leadID: leadIdOf(state.currentLead) });
    endCall();
    toast.success(`Call ended — ${label || 'Completed'}`, { duration: 3000 });
    return true;
  }, [endCall]);

  // ── 3. Reconcile with the server ──
  const reconcile = useCallback(async (reason) => {
    if (!enabled) return;
    if (useCallStore.getState().callStatus !== CALL_STATUS.CALLING) return;
    if (reconcilingRef.current) return;
    reconcilingRef.current = true;
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.nicephone.activeCall);
      const d = res.data?.response?.[0];
      if (!d || d.status !== 1) return;

      const state = useCallStore.getState();
      if (state.callStatus !== CALL_STATUS.CALLING) return;

      /**
       * A call the server still thinks is active, that this page did not start
       * and that the softphone has no session for, is a leftover from a page
       * that no longer exists — a refresh mid-call. The audio died with that
       * page, so waiting for the server to agree means waiting out the
       * two-hour active-call window (or forever, if the terminal webhook never
       * correlates) with the advisor pinned to a call that does not exist and
       * unable to dial. Release it and prompt for disposition.
       */
      if (isZombieCall({
        callStatus: state.callStatus,
        ownedBySession: state.ownedBySession,
        widgetReady: isNicePhoneWidgetReady(),
        hasLiveSession: hasLiveSipSession(),
        callingStatus: CALL_STATUS.CALLING,
      })) {
        leadFlowLog.effect('reconcile: stale call from a previous page load', {
          reason, serverActive: d.active,
        });
        markEnded(`reconcile:${reason}:stale-session`, 'Call interrupted');
        return;
      }

      if (d.active) return; // still ringing / talking

      const last = d.lastCall;
      const sameLead = last && String(last.callingLeadID) === leadIdOf(state.currentLead);
      leadFlowLog.effect('reconcile: no active call on server', { reason, lastStatus: last?.callingStatus });
      markEnded(`reconcile:${reason}`, sameLead ? last.callingStatus : 'Completed');
    } catch (err) {
      // Network hiccup — the next trigger will retry.
    } finally {
      reconcilingRef.current = false;
    }
  }, [enabled, markEnded]);

  // ── 1. SSE ──
  const handleCallEvent = useCallback((event) => {
    if (event?.type !== 'call_ended') return;
    const state = useCallStore.getState();
    if (state.callStatus === CALL_STATUS.CALLING) {
      markEnded('sse', event.disposition);
      return;
    }
    // Store lost the CALLING state (e.g. storage cleared) but the event is for
    // the lead on screen — still require a disposition.
    if (
      state.callStatus === CALL_STATUS.IDLE &&
      state.currentLead &&
      event.leadId != null &&
      String(event.leadId) === leadIdOf(state.currentLead)
    ) {
      leadFlowLog.effect('call_ended for on-screen lead while IDLE → endCall', { leadId: event.leadId });
      endCall();
      toast.success(`Call ended — ${event.disposition || 'Completed'}`, { duration: 3000 });
    }
  }, [markEnded, endCall]);

  useNicePhoneSSE(handleCallEvent, () => reconcile('sse-connected'), enabled);

  // ── 2. Vendor browser events ──
  useEffect(() => {
    if (!enabled) return undefined;
    const onIncoming = () => {
      if (window.__nicePhoneOutboundPending || useCallStore.getState().callStatus === CALL_STATUS.CALLING) {
        window.CRMCallHeaderSecure?.answerIncoming?.();
      }
    };
    const onStart = (event) => {
      setNicePhoneClickToCallMode(false);
      const state = useCallStore.getState();
      if (state.callStatus === CALL_STATUS.CALLING) return;
      const d = event.detail || {};
      startCall(
        state.currentLead || {
          leadName: d.caller || d.clid || 'Incoming',
          leadMobileNumber: d.caller || '',
        },
        null
      );
    };
    const onEnd = () => {
      setNicePhoneClickToCallMode(false);
      markEnded('sip');
    };
    window.addEventListener('nicedial:incoming-call', onIncoming);
    window.addEventListener('nicedial:call-start', onStart);
    window.addEventListener('nicedial:call-end', onEnd);
    return () => {
      window.removeEventListener('nicedial:incoming-call', onIncoming);
      window.removeEventListener('nicedial:call-start', onStart);
      window.removeEventListener('nicedial:call-end', onEnd);
    };
  }, [enabled, startCall, markEnded]);

  // Reconcile triggers
  useEffect(() => { reconcile('mount'); }, [reconcile]);
  useEffect(() => { reconcile('route-change'); }, [location.pathname, reconcile]);
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') reconcile('visible'); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, reconcile]);
  useEffect(() => {
    if (!enabled || callStatus !== CALL_STATUS.CALLING) return undefined;
    const t = setInterval(() => reconcile('heartbeat'), RECONCILE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [enabled, callStatus, reconcile]);

  if (!canUsePhone) return null;

  /**
   * A tab that could use the phone but does not hold it renders a notice rather
   * than nothing: the softphone is deliberately absent here, and silence would
   * read as a broken page.
   */
  if (!isPhoneLeader) {
    return (
      <div className={styles.otherTabNotice} role="status">
        Phone is active in another Relabix tab
      </div>
    );
  }


  const onLeadsPage = location.pathname === LEADS_PATH;
  const showPill = callStatus === CALL_STATUS.CALLING && !onLeadsPage;

  return (
    <>
      {/* Keep the SIP registration alive on every page */}
      <NicePhoneToolbar />

      {showPill && (
        <div className={styles.pill} role="status">
          <span className={styles.pillDot} />
          <PhoneCall size={16} />
          <span className={styles.pillText}>
            On call{currentLead?.leadName ? ` · ${currentLead.leadName}` : ''}
          </span>
          <button
            type="button"
            className={styles.pillHangup}
            onClick={() => {
              try { window.CRMCallHeaderSecure?.hangup?.(); } catch (e) { /* ignore */ }
            }}
            title="Hang up"
          >
            <PhoneOff size={14} />
          </button>
          <button
            type="button"
            className={styles.pillGo}
            onClick={() => navigate(LEADS_PATH)}
          >
            My Leads <ArrowRight size={14} />
          </button>
        </div>
      )}
    </>
  );
};

export default NicePhoneCallSync;
